'use client'

import { setCookie, deleteCookie } from 'cookies-next'
import { auth } from './firebase'
import {
  setSecureItem,
  getSecureItem,
  removeSecureItem
} from '@/app/components/Connectors/utils/secureStorage'

/**
 * Session management helpers for Firebase and NINX integrations
 */

interface NinxAuthResponse {
  status: boolean
  data: {
    expiresIn: number
  }
  already_exists: boolean
  ninx_kyc_status: string
  ninx_kyc_level: string
  ninx_vip_level: number | undefined
  ninx_keys: {
    api_key: string
    api_secret: string
  }
  message: string
}

/**
 * Refreshes the Firebase token and stores it as a secure cookie used by middleware.
 */
export const setAuthCookie = async (): Promise<void> => {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      return
    }

    const token = await currentUser.getIdToken(true)

    setCookie('firebase-auth-token', token, {
      maxAge: 60 * 60 * 24 * 7,
      path: '/',
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict'
    })
  } catch (error) {
    console.error('Error setting auth cookie:', error)
  }
}

export const removeNinxSessionCookie = (): void => {
  try {
    deleteCookie('sessionId', { path: '/' })
    deleteCookie('sessionID', { path: '/' })
    console.log('Requested removal of NINX session cookie')
  } catch (error) {
    console.error('Error removing NINX session cookie:', error)
  }
}

export const removeAuthCookie = (): void => {
  deleteCookie('firebase-auth-token', { path: '/' })
}

/**
 * Stores NINX API keys in secure storage.
 */
export const setNinxKeys = (apiKey: string, apiSecret: string): void => {
  try {
    setSecureItem('ninx_api_key', apiKey)
    setSecureItem('ninx_api_secret', apiSecret)
    console.log('NINX API keys stored securely')
  } catch (error) {
    console.error('Error storing NINX API keys:', error)
  }
}

/**
 * Gets NINX API keys from secure storage.
 */
export const getNinxKeys = (): {
  apiKey: string | null
  apiSecret: string | null
} => {
  try {
    return {
      apiKey: getSecureItem('ninx_api_key'),
      apiSecret: getSecureItem('ninx_api_secret')
    }
  } catch (error) {
    console.error('Error getting NINX API keys:', error)
    return { apiKey: null, apiSecret: null }
  }
}

/**
 * Stores Binance API keys in secure storage.
 */
export const setBinanceKeys = (apiKey: string, apiSecret: string): void => {
  try {
    setSecureItem('binance_api_key', apiKey)
    setSecureItem('binance_api_secret', apiSecret)
    console.log('Binance API keys stored securely')
  } catch (error) {
    console.error('Error storing Binance API keys:', error)
  }
}

/**
 * Gets Binance API keys from secure storage.
 */
export const getBinanceKeys = (): {
  apiKey: string | null
  apiSecret: string | null
} => {
  try {
    return {
      apiKey: getSecureItem('binance_api_key'),
      apiSecret: getSecureItem('binance_api_secret')
    }
  } catch (error) {
    console.error('Error getting Binance API keys:', error)
    return { apiKey: null, apiSecret: null }
  }
}

/**
 * Removes Binance API keys from secure storage.
 */
export const removeBinanceKeys = (): void => {
  try {
    removeSecureItem('binance_api_key')
    removeSecureItem('binance_api_secret')
    console.log('Binance API keys removed from secure storage')
  } catch (error) {
    console.error('Error removing Binance API keys:', error)
  }
}

/**
 * Removes NINX API keys from secure storage.
 */
export const removeNinxKeys = (): void => {
  try {
    removeSecureItem('ninx_api_key')
    removeSecureItem('ninx_api_secret')
    console.log('NINX API keys removed from secure storage')
  } catch (error) {
    console.error('Error removing NINX API keys:', error)
  }
}

/**
 * Stores FCM token in secure storage.
 */
export const setFcmToken = (fcmToken: string): void => {
  try {
    setSecureItem('fcm_token', fcmToken)
    console.log('FCM token stored securely')
  } catch (error) {
    console.error('Error storing FCM token:', error)
  }
}

/**
 * Gets FCM token from secure storage.
 */
export const getFcmToken = (): string | null => {
  try {
    return getSecureItem('fcm_token')
  } catch (error) {
    console.error('Error getting FCM token:', error)
    return null
  }
}

/**
 * Removes FCM token from secure storage.
 */
export const removeFcmToken = (): void => {
  try {
    removeSecureItem('fcm_token')
    console.log('FCM token removed from secure storage')
  } catch (error) {
    console.error('Error removing FCM token:', error)
  }
}

/**
 * Stores NINX user info in secure storage.
 */
export const setNinxUserInfo = (
  kycStatus: string,
  kycLevel: string,
  vipLevel: number | undefined
): void => {
  try {
    setSecureItem('ninx_kyc_status', kycStatus)
    setSecureItem('ninx_kyc_level', kycLevel)
    setSecureItem('ninx_vip_level', (vipLevel ?? 0).toString())
    console.log('NINX user info stored securely')
  } catch (error) {
    console.error('Error storing NINX user info:', error)
  }
}

/**
 * Gets NINX user info from secure storage.
 */
export const getNinxUserInfo = (): {
  kycStatus: string | null
  kycLevel: string | null
  vipLevel: number | null
} => {
  try {
    const vipLevelStr = getSecureItem('ninx_vip_level')
    return {
      kycStatus: getSecureItem('ninx_kyc_status'),
      kycLevel: getSecureItem('ninx_kyc_level'),
      vipLevel: vipLevelStr ? parseInt(vipLevelStr, 10) : null
    }
  } catch (error) {
    console.error('Error getting NINX user info:', error)
    return { kycStatus: null, kycLevel: null, vipLevel: null }
  }
}

/**
 * Removes NINX user info from secure storage.
 */
export const removeNinxUserInfo = (): void => {
  try {
    removeSecureItem('ninx_kyc_status')
    removeSecureItem('ninx_kyc_level')
    removeSecureItem('ninx_vip_level')
    console.log('NINX user info removed from secure storage')
  } catch (error) {
    console.error('Error removing NINX user info:', error)
  }
}

/**
 * Complete cleanup of all NINX-related data.
 * Called during sign out.
 */
export const clearNinxSession = (): void => {
  try {
    removeNinxSessionCookie()
    removeNinxKeys()
    removeFcmToken()
    removeNinxUserInfo()
    if (typeof window !== 'undefined') {
      localStorage.removeItem('ninx_session_expiry')
    }
    console.log('All NINX session data cleared')
  } catch (error) {
    console.error('Error clearing NINX session:', error)
  }
}

/**
 * Complete cleanup of all authentication data.
 * Called during full sign out.
 */
export const clearAllAuthData = (): void => {
  try {
    removeAuthCookie()
    clearNinxSession()
    console.log('All authentication data cleared')
  } catch (error) {
    console.error('Error clearing authentication data:', error)
  }
}

/**
 * Check if user has valid NINX session.
 * Relies on backend-managed sessionId cookie.
 */
export const hasValidNinxSession = (): boolean => {
  try {
    if (typeof window === 'undefined') {
      console.log('[Session Check] Window is undefined (SSR)')
      return false
    }

    // Primary check: localStorage expiry (set during successful login)
    const expiryStr = localStorage.getItem('ninx_session_expiry')
    console.log('[Session Check] ninx_session_expiry from localStorage:', expiryStr)
    
    if (expiryStr) {
      const expiryTime = parseInt(expiryStr, 10)
      const currentTime = Date.now()
      const timeUntilExpiry = expiryTime - currentTime
      const expiryDate = new Date(expiryTime).toISOString()
      const currentDate = new Date(currentTime).toISOString()
      
      console.log('[Session Check] Expiry time (UTC):', expiryDate)
      console.log('[Session Check] Current time (UTC):', currentDate)
      console.log('[Session Check] Time until expiry (ms):', timeUntilExpiry)
      console.log('[Session Check] Valid:', expiryTime > currentTime)
      
      if (!Number.isNaN(expiryTime) && expiryTime > currentTime) {
        console.log('[Session Check] ✅ Valid session found via localStorage expiry')
        return true
      } else {
        console.log('[Session Check] ❌ Session expired based on localStorage')
        return false
      }
    }

    // Secondary check: Try to detect sessionId cookie (may fail if httpOnly)
    const cookieString = document.cookie || ''
    const hasSessionId = /sessionId=/.test(cookieString)
    // console.log('[Session Check] Cookie string:', cookieString)
    console.log('[Session Check] Has sessionId cookie (accessible):', hasSessionId)
    
    if (hasSessionId) {
      console.log('[Session Check] ✅ Valid session found via sessionId cookie')
      return true
    }
    
    // Note: sessionId cookie may exist but be httpOnly (not accessible to JS)
    // In this case, we rely on server-side middleware validation
    console.log('[Session Check] ⚠️  sessionId cookie may exist but be httpOnly')
    console.log('[Session Check] ❌ No valid session found via client-side checks')
    return false
  } catch (error) {
    console.error('Error checking NINX session validity:', error)
    return false
  }
}

/**
 * Check if user has valid NINX keys (KYC completed).
 */
export const hasValidNinxKeys = (): boolean => {
  try {
    const { apiKey, apiSecret } = getNinxKeys()
    return !!(apiKey && apiSecret)
  } catch (error) {
    console.error('Error checking NINX keys validity:', error)
    return false
  }
}

// Export types
export type { NinxAuthResponse }
